import { IProduct } from '@kizi/comparator-core';
import { Div, Image, P } from '@kizi/core-components';
import { IconV2 } from '@kizi/shared-components';
import React from 'react';
import { ProductUsps } from '../../..';
import { sendCustomEvent } from '../../../components';
import { ComparisonSubmitButton } from '../../../components/comparison/ComparisonSubmitButton';
import { formatPrice } from '../../global';
import { onClick } from '../../product';

const onApplyClick = async (service, data: IProduct) => {
  const index = service.state.context.steps.findIndex(
    (el) => el.id === 'overview',
  );
  const interpreter = service.state.context.steps[index];
  const analyticsItem = data?.analyticsData;
  const googleAnalyticsObject = {
    event: 'add_to_cart',
    ecommerce: {
      currency: 'EUR',
      coupon: '',
      items: [analyticsItem],
    },
  };
  window?.dataLayer?.push(googleAnalyticsObject);
  if (data.affiliateUrl || data.affiliateUrl !== '') {
    sendCustomEvent('affiliate-apply', {
      selectedProduct: data,
    });
    const overviewIndex = service._state.context.steps.findIndex(
      (step) => step.id === 'productComparison',
    );
    service.send([
      {
        type: 'GOTO',
        index: overviewIndex,
      },
    ]);

    const url = new URL(window.location.href);
    if (url.searchParams.has('affiliateUrl')) {
      return;
    }
    url.searchParams.set('step', 'overview');

    window.open(`${url.toString()}&affiliateUrl`);
  } else {
    onClick(interpreter, service, data, index + 2);
  }
};

export const getProductComparisonOverview = (data: IProduct) => {
  const { image, name, priceInfo, subscriptionName, purchasableProduct } = data;
  const { total, totalFirstYearCost } = priceInfo;

  return [
    {
      content: (
        <Div display="flex" flexDirection="column" alignItems="center">
          <Div display="flex" variant="productLogoWrapper">
            {image ? (
              <>
                <Image
                  alt={`logo-${name}`}
                  height="100%"
                  src={image}
                  width="100%"
                />
              </>
            ) : (
              <Div height={40} />
            )}
          </Div>
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: <P variant="footnoteImportant">{subscriptionName}</P>,
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <Div display="flex" flexDirection="column" gridGap={1}>
          <Div
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <P>{'Per maand'}</P>
            <P variant="bodyImportant" fontSize="18px">
              {formatPrice(total || 0)}
            </P>
          </Div>
          <Div
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <P>{'Eerste jaar'}</P>
            <P variant="bodyImportant" fontSize="18px">
              {formatPrice(totalFirstYearCost || 0)}
            </P>
          </Div>
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: data?.durationForDiscount && data?.priceMonthWithDiscount && (
        <Div
          px={2}
          display="flex"
          width="fit-content"
          alignItems="center"
          borderRadius="100px"
          background="#FFC60D"
        >
          <P variant="footnoteRegular" lineHeight="24px">
            {`Eerste ${data.durationForDiscount} maanden voor € ${data.priceMonthWithDiscount}`}
          </P>
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: <ProductUsps data={data.usps} showWarnings={false} />,
      isVisible: true,
      toggleable: false,
    },
    {
      content: data?.averageDeliveryTimeInDays && (
        <Div
          display="flex"
          alignItems="center"
          justifyContent="center"
          gridGap={2}
        >
          <IconV2 width={17} height={16} name="delivery" fill="#A5A7AE" />
          <P variant="captionRegular" color="#A5A7AE" lineHeight="16px">
            {`Levertijd: ${data.averageDeliveryTimeInDays} dagen`}
          </P>
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <Div display="flex" justifyContent="center">
          <ComparisonSubmitButton
            width="100%!important"
            data={data}
            fieldName="overview.selectedProduct"
            nextIndex={3}
            onSubmitFn={onApplyClick}
            isProductEnabled={purchasableProduct}
          />
        </Div>
      ),
      isVisible: true,
      toggleable: false,
    },
  ];
};

export const getProductComparisonCosts = (data: IProduct) => {
  return [
    {
      content: `${data.durationInMonths} ${data.durationInMonths === '1' ? 'maand' : 'maanden'}`,
      isVisible: true,
      toggleable: false,
    },
    {
      content: data?.averageDeliveryTimeInDays
        ? `${data?.averageDeliveryTimeInDays} dagen`
        : '-',
      isVisible: true,
      toggleable: false,
    },

    {
      content: (
        <IconV2
          name={data?.switchingService ? 'green-check' : 'small-cancel'}
          width={28}
          height={28}
        />
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <IconV2
          name={data?.installationOptions ? 'green-check' : 'small-cancel'}
          width={28}
          height={28}
        />
      ),
      isVisible: true,
      toggleable: false,
    },
  ];
};

export const getProductComparisonInternetPackages = (data: IProduct) => {
  return [
    {
      content: data?.downloadSpeedInMb
        ? `${data.downloadSpeedInMb} Mbit/s`
        : '-',
      isVisible: true,
      toggleable: false,
    },
    {
      content: data?.uploadSpeedInMb ? `${data.uploadSpeedInMb} Mbit/s` : '-',
      isVisible: true,
      toggleable: false,
    },
    {
      content: data?.network || '-',
      isVisible: true,
      toggleable: false,
    },
  ];
};

export const getProductComparisonTelevisionPackages = (data: IProduct) => {
  return [
    {
      content: data?.tvChannels || '-',
      isVisible: true,
      toggleable: false,
    },
    {
      content: data?.hdChannels || '-',
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <IconV2
          name={data?.tvViaApp ? 'green-check' : 'small-cancel'}
          width={28}
          height={28}
        />
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <IconV2
          name={data?.reviewAndPause ? 'green-check' : 'small-cancel'}
          width={28}
          height={28}
        />
      ),
      isVisible: true,
      toggleable: false,
    },
  ];
};

export const getProductComparisonCallPackages = (data: IProduct) => {
  return [
    {
      content: (
        <IconV2
          name={
            data?.unlimitedCallingInNetherlands === 'true'
              ? 'green-check'
              : 'small-cancel'
          }
          width={28}
          height={28}
        />
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <IconV2
          name={
            data?.unlimitedCallingInEurope === 'true'
              ? 'green-check'
              : 'small-cancel'
          }
          width={28}
          height={28}
        />
      ),
      isVisible: true,
      toggleable: false,
    },
    {
      content: (
        <IconV2
          name={data?.numberPorting ? 'green-check' : 'small-cancel'}
          width={28}
          height={28}
        />
      ),
      isVisible: true,
      toggleable: false,
    },
  ];
};

export const osTriplePlayHelpers = {
  overview: getProductComparisonOverview,
  costs: getProductComparisonCosts,
  internetPackages: getProductComparisonInternetPackages,
  televisionPackages: getProductComparisonTelevisionPackages,
  callPackages: getProductComparisonCallPackages,
};
